import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Badge,
  Avatar,
  TextField,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Person, Circle, Search } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";

const daysAgo = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const ConversationsList = ({
  onSelectConversation,
  selectedConversationId,
}) => {
  const { conversations, users } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Create a sorted list of conversations with metadata
  const conversationsList = useMemo(() => {
    const list = Object.entries(conversations || {}).map(
      ([conversationId, messages]) => {
        const lastMessage = messages[messages.length - 1];
        const unreadCount = messages.filter(
          (msg) => msg.senderType === "user" && !msg.read,
        ).length;

        // Find user info
        const user = users?.find((u) => u.userId === conversationId);
        const displayName =
          user?.displayName ||
          user?.name ||
          user?.email?.split("@")[0] ||
          conversationId.substring(0, 8) + "...";

        return {
          conversationId,
          lastMessage,
          unreadCount,
          displayName,
          email: user?.email,
          messageCount: messages.length,
        };
      },
    );

    // Sort by most recent message
    list.sort((a, b) => b.lastMessage.sentAt - a.lastMessage.sentAt);
    return list;
  }, [conversations, users]);

  // Filter conversations based on search term
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversationsList;

    const lowerSearch = searchTerm.toLowerCase();
    return conversationsList.filter((conv) => {
      return (
        conv.displayName?.toLowerCase().includes(lowerSearch) ||
        conv.email?.toLowerCase().includes(lowerSearch) ||
        conv.conversationId?.toLowerCase().includes(lowerSearch) ||
        conv.lastMessage?.message?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [conversationsList, searchTerm]);

  // Paginate conversations
  const paginatedConversations = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredConversations.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredConversations, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredConversations.length / rowsPerPage);

  // Reset to page 1 when search term changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
    setPage(1);
  };

  if (conversationsList.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No conversations yet. Send a message to a user to start a
          conversation.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Search and Controls */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
          }}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: "0 !important" }}
          >
            {filteredConversations.length} conversation
            {filteredConversations.length !== 1 ? "s" : ""}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              label="Per page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Conversations List */}
      {filteredConversations.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No conversations match your search.
          </Typography>
        </Box>
      ) : (
        <>
          <List
            sx={{ p: 0, flex: 1, overflow: "auto", minHeight: 0 }}
            className="scroller"
          >
            {paginatedConversations.map(
              ({
                conversationId,
                lastMessage,
                unreadCount,
                displayName,
                email,
              }) => (
                <ListItem
                  key={conversationId}
                  disablePadding
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <ListItemButton
                    onClick={() => onSelectConversation(conversationId)}
                    selected={selectedConversationId === conversationId}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: "#e8f5e9",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Badge
                        badgeContent={unreadCount}
                        color="error"
                        overlap="circular"
                      >
                        <Avatar
                          sx={{ bgcolor: "#197b68", width: 40, height: 40 }}
                        >
                          <Person />
                        </Avatar>
                      </Badge>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={unreadCount > 0 ? "bold" : "normal"}
                            sx={{ mb: "0 !important" }}
                            noWrap
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: "0 !important" }}
                          >
                            {daysAgo(lastMessage.sentAt)}
                          </Typography>
                        </Box>

                        {email && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: "0 !important" }}
                            noWrap
                          >
                            {email}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {lastMessage.senderType === "user" && (
                            <Circle
                              sx={{
                                fontSize: 8,
                                color: unreadCount > 0 ? "#197b68" : "grey.400",
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: "0 !important",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {lastMessage.senderType === "admin" ? "You: " : ""}
                            {lastMessage.message.substring(0, 50)}
                            {lastMessage.message.length > 50 ? "..." : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ),
            )}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ConversationsList;
